import { useEffect, useRef } from 'react';

/**
 * Hook theo dõi một phần tử khi nó đi vào viewport lần đầu tiên và kích hoạt tracking.
 * @param callback Hàm được gọi khi phần tử đi vào viewport.
 * @param once Nếu true, chỉ kích hoạt callback một lần duy nhất.
 * @param threshold Ngưỡng hiển thị (0.0 đến 1.0).
 * @returns Ref để gán vào phần tử HTML bạn muốn theo dõi.
 */
const useInViewTracking = (callback: any, once = true, threshold = 0.1) => {
  const targetRef = useRef(null);
  const tracked = useRef(false); // Theo dõi xem sự kiện đã được kích hoạt chưa

  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      return;
    }

    const currentRef = targetRef.current;
    if (!currentRef) return;

    // Nếu đã theo dõi một lần và cấu hình là 'once', thì không cần thiết lập observer nữa
    if (once && tracked.current) return;

    const observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Phần tử đã đi vào viewport
            callback();

            // Nếu chỉ tracking một lần, ngắt kết nối observer
            if (once) {
              observer.unobserve(entry.target);
              tracked.current = true;
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: threshold,
      },
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
        targetRef.current = null;
        tracked.current = false;
      }
    };
  }, [callback, once, threshold]);

  return targetRef;
};

export default useInViewTracking;
